import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { track, reportNonCritical } from "@/lib/analytics";
import { TAP_DOWN } from "@/lib/motion";
import { BRAND } from "@/data/content";
import { STEPS } from "@/data/onboarding";
import { ProgressBar } from "./OnboardingPrimitives";
import StepGoal from "./steps/StepGoal";
import StepBusiness from "./steps/StepBusiness";
import StepVoice from "./steps/StepVoice";
import StepChannels from "./steps/StepChannels";

const TOTAL_STEPS = STEPS.length;

function nextButtonContent(submitting, isFinalStep) {
  if (submitting) {
    return (
      <>
        <Loader2 size={14} className="animate-spin" /> Saving&hellip;
      </>
    );
  }
  if (isFinalStep) {
    return (
      <>
        Finish &amp; see plans <ArrowRight size={14} />
      </>
    );
  }
  return (
    <>
      Continue <ArrowRight size={14} />
    </>
  );
}

function initialState(user) {
  const bv = user?.brand_voice || {};
  return {
    goal: bv.goal || "",
    business_name: bv.business_name || "",
    business_type: bv.business_type || "",
    audience: bv.audience || "",
    voice_traits: bv.voice_traits || [],
    voice_sample: bv.voice_sample || "",
    channels: bv.channels || [],
  };
}

function canAdvanceFor(idx, state) {
  if (idx === 0) return !!state.goal;
  if (idx === 1) return !!state.business_name?.trim() && !!state.business_type;
  if (idx === 2) return state.voice_traits.length > 0;
  if (idx === 3) return state.channels.length > 0;
  return true;
}

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [idx, setIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [state, setState] = useState(() => initialState(user));

  const update = (k, v) => setState((s) => ({ ...s, [k]: v }));
  const toggleArrayItem = (k, id) =>
    setState((s) => {
      const cur = s[k] || [];
      return { ...s, [k]: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] };
    });

  const persist = async (markComplete) => {
    const payload = { ...state, onboarding_complete: !!markComplete };
    const { data } = await api.post("/auth/onboarding", payload);
    setUser(data);
    return data;
  };

  const next = async () => {
    if (!canAdvanceFor(idx, state)) return;
    track("Onboarding Step Completed", { step: STEPS[idx].id, idx });
    if (idx < TOTAL_STEPS - 1) {
      try {
        await persist(false);
      } catch (err) {
        reportNonCritical(err, "onboarding.partialSave");
      }
      setIdx(idx + 1);
      return;
    }
    setSubmitting(true);
    try {
      await persist(true);
      track("Onboarding Completed", {
        traits: state.voice_traits.length,
        channels: state.channels.length,
      });
      toast.success("Your Studio is calibrated. Time to pick a plan.");
      navigate("/pricing", { state: { from: "onboarding" } });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save - please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const back = () => idx > 0 && setIdx(idx - 1);

  const skip = async () => {
    try {
      await persist(true);
    } catch (err) {
      reportNonCritical(err, "onboarding.skip");
    }
    toast("Skipped - you can refine your studio later from Settings.");
    navigate("/pricing");
  };

  return (
    <div
      data-testid="onboarding-wizard"
      className="relative min-h-screen bg-white text-neutral-900 flex flex-col overflow-hidden"
    >
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="aurora-orb aurora-orb-magenta orb-anim-a" style={{ width: 380, height: 380, top: -120, left: -140, opacity: 0.16 }} />
        <div className="aurora-orb aurora-orb-azure orb-anim-b" style={{ width: 340, height: 340, bottom: -120, right: -120, opacity: 0.14 }} />
      </div>

      <header className="relative z-10 border-b border-neutral-200/70">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 h-16 sm:h-20 flex items-center justify-between gap-6">
          <Link to="/" data-testid="onboarding-logo" className="flex items-baseline gap-2 sm:gap-3">
            <span className="font-serif text-lg sm:text-2xl tracking-tight text-neutral-900">
              {BRAND.wordmark.lead}
            </span>
            <span className="font-serif italic text-lg sm:text-2xl text-aurora">
              {BRAND.wordmark.tail}
            </span>
          </Link>
          <button
            type="button"
            data-testid="onboarding-skip"
            onClick={skip}
            className="text-[11px] sm:text-xs uppercase tracking-[0.2em] text-neutral-500 hover:text-neutral-900"
          >
            Skip for now
          </button>
        </div>
      </header>

      <main className="relative z-10 flex-1 flex flex-col">
        <div className="max-w-[1400px] mx-auto w-full px-5 sm:px-6 lg:px-12 pt-8 sm:pt-10">
          <ProgressBar currentIdx={idx} />
        </div>

        <div className="flex-1 max-w-3xl w-full mx-auto px-5 sm:px-6 lg:px-12 py-12 sm:py-16 lg:py-20">
          {idx === 0 && <StepGoal state={state} update={update} />}
          {idx === 1 && <StepBusiness state={state} update={update} />}
          {idx === 2 && <StepVoice state={state} update={update} toggleArrayItem={toggleArrayItem} />}
          {idx === 3 && <StepChannels state={state} toggleArrayItem={toggleArrayItem} />}
        </div>

        <footer className="relative z-10 border-t border-neutral-200/70">
          <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-12 py-5 sm:py-6 flex items-center justify-between gap-4">
            <button
              type="button"
              data-testid="onboarding-back"
              onClick={back}
              disabled={idx === 0}
              className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-900 disabled:opacity-30 disabled:pointer-events-none"
            >
              <ArrowLeft size={14} /> Back
            </button>
            <motion.button
              type="button"
              data-testid="onboarding-next"
              onClick={next}
              disabled={!canAdvanceFor(idx, state) || submitting}
              whileTap={TAP_DOWN}
              className="btn-aurora disabled:opacity-40 disabled:pointer-events-none"
            >
              {nextButtonContent(submitting, idx === TOTAL_STEPS - 1)}
            </motion.button>
          </div>
        </footer>
      </main>
    </div>
  );
}
